/**
 * MessageThread — scrollable list of messages for the active chat.
 *
 * Iter 152.2 (Feb 2026, Yash) — extracted from ChatsPage.jsx to reduce that
 * file's size and isolate the rendering loop. The component is intentionally
 * "dumb": parent (ChatsPage) owns all data + callbacks; this only handles
 * the loading / empty / grouped-by-date layouts and per-message ErrorBoundary
 * wrapping.
 */
import { MessageCircle } from "lucide-react";
import { ErrorBoundary } from "../ErrorBoundary";
import MessageBubble from "./MessageBubble";
import { groupMessagesByDate, formatFullTime } from "./utils";

const MessageThread = ({
  loading,
  messages,
  msgEndRef,
  onImageClick,
  onImageSearch,
  onReply,
  onResend,
  onVerifyPayment,
}) => {
  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="message-thread">
        <div className="flex items-center justify-center h-full">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="message-thread">
        <div className="flex items-center justify-center h-full text-[--text-muted]">
          <div className="text-center">
            <MessageCircle size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-xs">No messages yet</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="message-thread">
      <ErrorBoundary label="messages list">
        {groupMessagesByDate(messages).map(({ date, msgs }) => (
          <div key={date}>
            <div className="flex justify-center my-3">
              <span className="text-[10px] text-[--text-muted] bg-white/80 px-3 py-1 rounded-full shadow-sm">
                {date}
              </span>
            </div>
            {msgs.map((msg) => (
              <ErrorBoundary key={msg.id} label={`message ${msg.id}`}>
                <MessageBubble
                  message={msg}
                  formatTime={formatFullTime}
                  onImageClick={onImageClick}
                  onImageSearch={onImageSearch}
                  onReply={onReply}
                  onResend={onResend}
                  onVerifyPayment={onVerifyPayment}
                />
              </ErrorBoundary>
            ))}
          </div>
        ))}
        <div ref={msgEndRef} />
      </ErrorBoundary>
    </div>
  );
};

export default MessageThread;
